import { computed } from 'vue'
import { inject } from 'vue'
import { isProxy } from 'vue'
import { provide } from 'vue'
import { reactive } from 'vue'
import { ref } from 'vue'
import { toRefs } from 'vue'
import { unref } from 'vue'
import type { ComputedRef, Ref, App } from 'vue'
import { loadRouteLocation, useRouter } from 'vue-router'
import type { RouteLocationNamedRaw, RouteLocationPathRaw, RouteLocationRaw, } from 'vue-router'

import RemoteContent from '#src/vue/RemoteContent.vue'

import { usePopupRouteData } from './popupRouteData.mts'
const INJECTION_SYMBOL = Symbol('AutoPopupData')

export type AutoPopupData = {
    show: Ref<boolean>,
    to: Ref<RouteLocationRaw>,
}

type AutoPopupInjectedData = {
    popups: AutoPopupData[],
    currentAutoPopup: {
        show: boolean
        to: RouteLocationRaw
        close: (() => void)
    }
    autoPopupExists: ComputedRef<boolean>
    registerAutoPopup: ((popup: AutoPopupData) => void)
    autoPopupComponentData: { component: any, params: any}
}

/**
 * _generateAutoPopupData
 *
 * If we install the autopopup behaviour using a plugin,
 * then we want to access the root popup data directly.
 *
 * Otherwise, we want to use the handy provideAutoPopupData function.
 */
function _generateAutoPopupData () {

    const popups: AutoPopupData[] = reactive([])

    const currentAutoPopup = computed(() => {
        const _data = popups.slice().map(popup => {
            const { show, to } = popup
            // @ts-ignore
            const _close = () => (isProxy(popup) ? popup.show = false : popup.show.value = false)
            return { show: unref(show), to: unref(to), close: _close }
        })

        const active = _data.find(({show}) => show)

        // console.debug("Calculating current auto popup", active, _data)

        return active
    })

    const autoPopupExists = computed( () => unref(currentAutoPopup) !== undefined )

    const registerAutoPopup = (data: AutoPopupData) => {
        try {
            const _data = (isProxy(data))
                ? toRefs(data)
                : data
            popups.push(_data)
        } catch (error) {
            console.error("Error registering auto popup", (error as Error).message)
        }
    }


    const injectionData = { popups, currentAutoPopup, autoPopupExists, registerAutoPopup, }
    return { symbol: INJECTION_SYMBOL, injectionData }
}

export function installAutoPopupData (app: App) {
    const { symbol, injectionData } = _generateAutoPopupData()
    app.provide(symbol, injectionData)
}

export function provideAutoPopupData () {
    const { symbol, injectionData } = _generateAutoPopupData()
    provide(symbol, injectionData)
    return injectionData
}

export function useAutoPopupData () {
    const router = useRouter()
    const { parsePopup } = usePopupRouteData()

    const data = inject<AutoPopupInjectedData | undefined>(INJECTION_SYMBOL, undefined)

    const { currentAutoPopup } = data ?? {}

    let _resolved = ref<boolean>(true)

    const routeLocation = computed(() => {
        const to = unref(currentAutoPopup)?.to
        return to ? parsePopup(to) : to
    })
    const autoPopupComponentData = computed(() => {
        unref(_resolved) // react to route location resolution
        const _to = parsePopup(routeLocation)

        if ( _to === undefined ) {
            return undefined
        }

        if (typeof _to === 'string') {
            return {
                component: RemoteContent,
                params: { url: _to },
            }
        }

        try {
            const route = router.resolve(_to)
            const match = route.matched[1]

            if (!match) {
                console.warn("Sanity check failed: Expected to find popup match as the second matched route.", route)
                return undefined
            }

            const component = match.components?.default
            const params = route.params

            if (!component) {
                console.warn("Sanity check failed: AutoPopup match doesn't have a default component.")
                return undefined
            }

            if (typeof component === 'function') {
                Promise.resolve()
                    .then(() => _resolved.value = false)
                    .then(() => loadRouteLocation(route))
                    .then(() => _resolved.value = true)
                console.debug("loading route location before mounting component")
                return undefined
            }

            console.debug("Calculated auto popup component data", { params })
            return { component, params }
        } catch (error) {
            console.error("Error generating AutoPopup component data", _to, (error as Error).message)
            return undefined
        }
    })

    return ({
        ...data,
        autoPopupComponentData
    })
}
